import { getAppCurrencyPrefix } from '../lib/currency';
import {
  AccommodationBooking,
  AccommodationBookingEvent,
  AccommodationBookingStatus,
  AccommodationClient,
  AccommodationDashboardStats,
  AccommodationGuest,
  AccommodationGuestDetails,
  AccommodationPayment,
  AccommodationProperty,
  AccommodationRatePlan,
  AccommodationRoom,
  AccommodationRoomType,
  BookingFilters,
  BookingSource,
  Country,
  CreateBookingRequest,
  CreatePaymentRequest,
  CreateRatePlanRequest,
  CreateRoomRequest,
  CreateRoomTypeRequest,
  EarlyCheckoutParams,
  GuestReviewAnalytics,
  ReviewMonthlyTrend,
  ReviewStats,
  UpdateBookingRequest,
  UpdatePropertyRequest,
  UpdateRatePlanRequest,
  UpdateRoomRequest,
  UpdateRoomTypeRequest,
} from '../types/accommodation';
import { supabase } from './supabase';

// ----------------------------------------------------------------
// Property
// ----------------------------------------------------------------

export async function fetchProperty(
  outletId: string,
): Promise<AccommodationProperty | null> {
  const { data, error } = await supabase
    .from('accommodation_properties')
    .select('*')
    .eq('outlet_id', outletId)
    .maybeSingle();

  if (error) throw new Error(error.message);
  return data;
}

export async function createProperty(
  outletId: string,
  name: string,
): Promise<AccommodationProperty> {
  const { data, error } = await supabase
    .from('accommodation_properties')
    .insert({ outlet_id: outletId, name, currency: getAppCurrencyPrefix() })
    .select()
    .single();

  if (error) throw new Error(error.message);
  return data;
}

export async function updateProperty(
  id: string,
  updates: UpdatePropertyRequest,
): Promise<AccommodationProperty> {
  const { data, error } = await supabase
    .from('accommodation_properties')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) throw new Error(error.message);
  return data;
}

async function resizeImageToBlob(
  file: File,
  maxBytes = 1_000_000,
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const MAX_DIM = 1920;
      let { width, height } = img;
      if (width > MAX_DIM || height > MAX_DIM) {
        if (width >= height) {
          height = Math.round((height / width) * MAX_DIM);
          width = MAX_DIM;
        } else {
          width = Math.round((width / height) * MAX_DIM);
          height = MAX_DIM;
        }
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Canvas not supported'));
        return;
      }
      ctx.drawImage(img, 0, 0, width, height);

      const tryQuality = (quality: number) => {
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Image conversion failed'));
              return;
            }
            if (blob.size <= maxBytes || quality <= 0.4) {
              resolve(blob);
            } else {
              tryQuality(Math.round((quality - 0.1) * 10) / 10);
            }
          },
          'image/jpeg',
          quality,
        );
      };
      tryQuality(0.85);
    };
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = URL.createObjectURL(file);
  });
}

export async function uploadPropertyImage(
  propertyId: string,
  file: File,
): Promise<string> {
  const blob = await resizeImageToBlob(file);
  const path = `${propertyId}/cover.jpg`;
  const { error } = await supabase.storage
    .from('properties')
    .upload(path, blob, { contentType: 'image/jpeg', upsert: true });
  if (error) throw new Error(error.message);
  const { data } = supabase.storage.from('properties').getPublicUrl(path);
  return data.publicUrl;
}

export async function fetchAllCompanyProperties(): Promise<
  AccommodationProperty[]
> {
  const { data, error } = await supabase
    .from('accommodation_properties')
    .select('*')
    .order('name');
  if (error) throw new Error(error.message);
  return data ?? [];
}

// ----------------------------------------------------------------
// Room types
// ----------------------------------------------------------------

export async function fetchRoomTypes(
  propertyId: string,
): Promise<AccommodationRoomType[]> {
  const { data, error } = await supabase
    .from('accommodation_room_types')
    .select('*')
    .eq('property_id', propertyId)
    .order('name');

  if (error) throw new Error(error.message);
  return data ?? [];
}

export async function createRoomType(
  request: CreateRoomTypeRequest,
): Promise<AccommodationRoomType> {
  const { data, error } = await supabase
    .from('accommodation_room_types')
    .insert(request)
    .select()
    .single();

  if (error) throw new Error(error.message);
  return data;
}

export async function updateRoomType(
  id: string,
  updates: UpdateRoomTypeRequest,
): Promise<AccommodationRoomType> {
  const { data, error } = await supabase
    .from('accommodation_room_types')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) throw new Error(error.message);
  return data;
}

// ----------------------------------------------------------------
// Rooms
// ----------------------------------------------------------------

export async function fetchRooms(
  propertyId: string,
): Promise<AccommodationRoom[]> {
  const { data, error } = await supabase
    .from('accommodation_rooms')
    .select('*, room_type:accommodation_room_types(*)')
    .eq('property_id', propertyId)
    .order('room_number');

  if (error) throw new Error(error.message);
  return (data ?? []) as AccommodationRoom[];
}

export async function createRoom(
  request: CreateRoomRequest,
): Promise<AccommodationRoom> {
  const { data, error } = await supabase
    .from('accommodation_rooms')
    .insert(request)
    .select('*, room_type:accommodation_room_types(*)')
    .single();

  if (error) throw new Error(error.message);
  return data as AccommodationRoom;
}

export async function updateRoom(
  id: string,
  updates: UpdateRoomRequest,
): Promise<AccommodationRoom> {
  const { data, error } = await supabase
    .from('accommodation_rooms')
    .update(updates)
    .eq('id', id)
    .select('*, room_type:accommodation_room_types(*)')
    .single();

  if (error) throw new Error(error.message);
  return data as AccommodationRoom;
}

// ----------------------------------------------------------------
// Rate plans
// ----------------------------------------------------------------

export async function fetchRatePlans(
  propertyId: string,
): Promise<AccommodationRatePlan[]> {
  const { data, error } = await supabase
    .from('accommodation_rate_plans')
    .select('*')
    .eq('property_id', propertyId)
    .order('name');

  if (error) throw new Error(error.message);
  return data ?? [];
}

export async function createRatePlan(
  request: CreateRatePlanRequest,
): Promise<AccommodationRatePlan> {
  const { data, error } = await supabase
    .from('accommodation_rate_plans')
    .insert(request)
    .select()
    .single();

  if (error) throw new Error(error.message);
  return data;
}

export async function updateRatePlan(
  id: string,
  updates: UpdateRatePlanRequest,
): Promise<AccommodationRatePlan> {
  const { data, error } = await supabase
    .from('accommodation_rate_plans')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) throw new Error(error.message);
  return data;
}

// ----------------------------------------------------------------
// Bookings
// ----------------------------------------------------------------

const BOOKING_SELECT = `
  *,
  client:clients(
    id, company_id, first_name, last_name, email, contact, address,
    nationality, date_of_birth, id_type, id_number, accommodation_notes,
    created_at, country_id,
    country:countries(id, name, iso_code, phone_code)
  ),
  booking_rooms:accommodation_booking_rooms(
    *,
    room:accommodation_rooms(*, room_type:accommodation_room_types(*))
  ),
  booking_source:accommodation_booking_sources(id, name, icon, sort_order, is_active),
  guest_country:countries(id, name, iso_code, phone_code)
`;

export async function fetchBookings(
  propertyId: string,
  filters: BookingFilters = {},
): Promise<AccommodationBooking[]> {
  let query = supabase
    .from('accommodation_bookings')
    .select(BOOKING_SELECT)
    .eq('property_id', propertyId)
    .order('check_in', { ascending: false });

  if (filters.status) {
    query = query.eq('status', filters.status);
  }
  if (filters.check_in_from) {
    query = query.gte('check_in', filters.check_in_from);
  }
  if (filters.check_in_to) {
    query = query.lte('check_in', filters.check_in_to);
  }
  if (filters.stay_from) {
    query = query.gt('check_out', filters.stay_from);
  }
  if (filters.stay_to) {
    query = query.lte('check_in', filters.stay_to);
  }
  if (filters.check_out_before) {
    query = query.lt('check_out', filters.check_out_before);
  }

  const { data, error } = await query;
  if (error) throw new Error(error.message);
  return (data ?? []) as AccommodationBooking[];
}

export async function fetchBookingById(
  id: string,
): Promise<AccommodationBooking> {
  const { data, error } = await supabase
    .from('accommodation_bookings')
    .select(BOOKING_SELECT)
    .eq('id', id)
    .single();

  if (error) throw new Error(error.message);
  return data as AccommodationBooking;
}

export async function createBooking(
  request: CreateBookingRequest,
): Promise<AccommodationBooking> {
  const { rooms, ...bookingPayload } = request;

  const { data: booking, error: bookingError } = await supabase
    .from('accommodation_bookings')
    .insert(bookingPayload)
    .select()
    .single();

  if (bookingError) throw new Error(bookingError.message);

  const roomRows = rooms.map((r) => ({ ...r, booking_id: booking.id }));
  const { error: roomsError } = await supabase
    .from('accommodation_booking_rooms')
    .insert(roomRows);

  if (roomsError) throw new Error(roomsError.message);

  return fetchBookingById(booking.id);
}

export async function updateBookingStatus(
  id: string,
  status: AccommodationBookingStatus,
  notes?: string,
): Promise<AccommodationBooking> {
  const { error } = await supabase
    .from('accommodation_bookings')
    .update({ status, ...(notes ? { notes } : {}) })
    .eq('id', id);

  if (error) throw new Error(error.message);
  return fetchBookingById(id);
}

export async function updateBooking(
  id: string,
  updates: UpdateBookingRequest,
): Promise<AccommodationBooking> {
  const { error } = await supabase
    .from('accommodation_bookings')
    .update(updates)
    .eq('id', id);

  if (error) throw new Error(error.message);
  return fetchBookingById(id);
}

// ----------------------------------------------------------------
// Booking events
// ----------------------------------------------------------------

export async function fetchBookingEvents(
  bookingId: string,
): Promise<AccommodationBookingEvent[]> {
  const { data, error } = await supabase
    .from('accommodation_booking_events')
    .select('*, triggered_by_user:users(first_name, last_name)')
    .eq('booking_id', bookingId)
    .order('created_at', { ascending: true });

  if (error) throw new Error(error.message);
  return (data ?? []) as AccommodationBookingEvent[];
}

// ----------------------------------------------------------------
// Clients (shared with POS)
// ----------------------------------------------------------------

export async function searchClients(
  companyId: number,
  query: string,
): Promise<AccommodationClient[]> {
  const { data, error } = await supabase
    .from('clients')
    .select(
      'id, company_id, first_name, last_name, email, contact, address, nationality, date_of_birth, id_type, id_number, accommodation_notes, created_at, country_id',
    )
    .eq('company_id', companyId)
    .or(
      `first_name.ilike.%${query}%,last_name.ilike.%${query}%,email.ilike.%${query}%,contact.ilike.%${query}%`,
    )
    .order('first_name')
    .limit(20);

  if (error) throw new Error(error.message);
  return (data ?? []) as AccommodationClient[];
}

const ACCOMMODATION_CLIENT_SELECT =
  'id, company_id, first_name, last_name, email, contact, address, tin_number, nationality, date_of_birth, id_type, id_number, accommodation_notes, created_at, country_id, country:countries(id, name, iso_code, phone_code)';

export async function createClient(
  companyId: number,
  input: Omit<AccommodationClient, 'id' | 'company_id' | 'created_at'>,
): Promise<AccommodationClient> {
  const { data, error } = await supabase
    .from('clients')
    .insert({ ...input, company_id: companyId })
    .select(ACCOMMODATION_CLIENT_SELECT)
    .single();

  if (!error) return data as unknown as AccommodationClient;

  // Duplicate email for this company — return the existing client record
  if (error.code === '23505' && input.email) {
    const { data: existing, error: fetchError } = await supabase
      .from('clients')
      .select(ACCOMMODATION_CLIENT_SELECT)
      .eq('company_id', companyId)
      .eq('email', input.email)
      .single();

    if (fetchError) throw new Error(fetchError.message);
    return existing as unknown as AccommodationClient;
  }

  throw new Error(error.message);
}

export async function fetchAccommodationClientById(
  id: string,
): Promise<AccommodationClient> {
  const { data, error } = await supabase
    .from('clients')
    .select(ACCOMMODATION_CLIENT_SELECT)
    .eq('id', id)
    .single();

  if (error) throw new Error(error.message);
  return data as unknown as AccommodationClient;
}

export async function updateClientAccommodationDetails(
  id: string,
  updates: Partial<
    Pick<
      AccommodationClient,
      | 'first_name'
      | 'last_name'
      | 'email'
      | 'contact'
      | 'address'
      | 'tin_number'
      | 'country_id'
      | 'date_of_birth'
      | 'id_type'
      | 'id_number'
      | 'accommodation_notes'
    >
  >,
): Promise<AccommodationClient> {
  const { data, error } = await supabase
    .from('clients')
    .update(updates)
    .eq('id', id)
    .select(ACCOMMODATION_CLIENT_SELECT)
    .single();

  if (error) throw new Error(error.message);
  return data as unknown as AccommodationClient;
}

// ----------------------------------------------------------------
// Availability check: returns room IDs that are booked for
// the given date range so the UI can filter them out.
// ----------------------------------------------------------------
export async function fetchBookedRoomIds(
  propertyId: string,
  checkIn: string,
  checkOut: string,
  excludeBookingId?: string,
): Promise<string[]> {
  let query = supabase
    .from('accommodation_bookings')
    .select('booking_rooms:accommodation_booking_rooms(room_id)')
    .eq('property_id', propertyId)
    .not('status', 'in', '("cancelled","checked_out","no_show")')
    .lt('check_in', checkOut)
    .gt('check_out', checkIn);

  if (excludeBookingId) {
    query = query.neq('id', excludeBookingId);
  }

  const { data, error } = await query;
  if (error) throw new Error(error.message);

  const ids: string[] = [];
  (data ?? []).forEach((b: { booking_rooms: { room_id: string }[] }) => {
    b.booking_rooms.forEach((r) => ids.push(r.room_id));
  });
  return ids;
}

// ----------------------------------------------------------------
// Booking sources (global, no auth filter needed)
// ----------------------------------------------------------------
export async function fetchBookingSources(): Promise<BookingSource[]> {
  const { data, error } = await supabase
    .from('accommodation_booking_sources')
    .select('id, name, icon, sort_order, is_active')
    .eq('is_active', true)
    .order('sort_order');
  if (error) throw new Error(error.message);
  return (data ?? []) as BookingSource[];
}

// ----------------------------------------------------------------
// Countries (global lookup for guest nationality)
// ----------------------------------------------------------------
export async function fetchCountries(): Promise<Country[]> {
  const { data, error } = await supabase
    .from('countries')
    .select('id, name, iso_code, phone_code')
    .order('name');
  if (error) throw new Error(error.message);
  return (data ?? []) as Country[];
}

// ----------------------------------------------------------------
// Booking payments
// ----------------------------------------------------------------
export async function fetchBookingPayments(
  bookingId: string,
): Promise<AccommodationPayment[]> {
  const { data, error } = await supabase
    .from('accommodation_booking_payments')
    .select(
      'id, booking_id, amount, payment_method_id, notes, paid_at, recorded_by, created_at',
    )
    .eq('booking_id', bookingId)
    .order('paid_at', { ascending: false });

  if (error) throw new Error(error.message);
  return (data ?? []) as AccommodationPayment[];
}

export interface PropertyPaymentRow {
  id: string;
  booking_id: string;
  amount: number;
  payment_method_id: number;
  payment_method_name: string | null;
  notes: string | null;
  paid_at: string;
  client_first_name: string | null;
  client_last_name: string | null;
}

export interface PropertyPaymentFilters {
  dateFrom?: string | null;
  dateTo?: string | null;
}

export async function fetchPropertyPayments(
  propertyId: string,
  filters: PropertyPaymentFilters = {},
): Promise<PropertyPaymentRow[]> {
  const { data: bookings, error: bookingsError } = await supabase
    .from('accommodation_bookings')
    .select('id')
    .eq('property_id', propertyId);

  if (bookingsError) throw new Error(bookingsError.message);

  const bookingIds = (bookings ?? []).map((b: any) => b.id as string);
  if (bookingIds.length === 0) return [];

  let query = supabase
    .from('accommodation_booking_payments')
    .select(
      'id, booking_id, amount, payment_method_id, notes, paid_at, booking:accommodation_bookings!booking_id(client:clients(first_name,last_name)), payment_method:payment_methods(name)',
    )
    .in('booking_id', bookingIds)
    .order('paid_at', { ascending: false });

  if (filters.dateFrom) {
    query = query.gte('paid_at', `${filters.dateFrom}T00:00:00`);
  }
  if (filters.dateTo) {
    query = query.lte('paid_at', `${filters.dateTo}T23:59:59`);
  }

  const { data, error } = await query;
  if (error) throw new Error(error.message);

  return (data ?? []).map((p: any) => ({
    id: p.id as string,
    booking_id: p.booking_id as string,
    amount: p.amount as number,
    payment_method_id: p.payment_method_id as number,
    payment_method_name: (p.payment_method?.name as string) ?? null,
    notes: (p.notes as string | null) ?? null,
    paid_at: p.paid_at as string,
    client_first_name: (p.booking?.client?.first_name as string) ?? null,
    client_last_name: (p.booking?.client?.last_name as string) ?? null,
  }));
}

export async function recordBookingPayment(
  request: CreatePaymentRequest,
): Promise<AccommodationPayment> {
  const { data, error } = await supabase
    .from('accommodation_booking_payments')
    .insert({
      booking_id: request.booking_id,
      amount: request.amount,
      payment_method_id: request.payment_method_id,
      notes: request.notes ?? null,
    })
    .select(
      'id, booking_id, amount, payment_method_id, notes, paid_at, recorded_by, created_at',
    )
    .single();

  if (error) throw new Error(error.message);
  return data as AccommodationPayment;
}

// ----------------------------------------------------------------
// Audited financial corrections (pending/confirmed bookings only)
// ----------------------------------------------------------------
// Each RPC enforces permission, booking status and a reason server-side,
// writes an accommodation_booking_events row and returns its id. The change
// email is sent from that event, fire-and-forget: a mail failure must not
// undo or mask a saved correction.

async function sendBookingChangeEmail(eventId: string): Promise<void> {
  const { data, error } = await supabase.functions.invoke(
    'send-booking-change-email',
    { body: { event_id: eventId } },
  );
  if (error) throw new Error(error.message);
  // 200 with `skipped` means nothing was sent (e.g. no recipients configured).
  if (data?.skipped) {
    // eslint-disable-next-line no-console
    console.warn('[accommodation] change email skipped:', data.skipped);
  }
}

function notifyBookingFinancialChange(eventId: string): void {
  sendBookingChangeEmail(eventId).catch((err) => {
    // eslint-disable-next-line no-console
    console.error('[accommodation] change email failed:', err);
  });
}

export async function updateBookingRoomRates(
  bookingId: string,
  rates: { booking_room_id: string; price_per_night: number }[],
  reason: string,
): Promise<void> {
  const { data, error } = await supabase.rpc('update_booking_room_rates', {
    p_booking_id: bookingId,
    p_rates: rates,
    p_reason: reason,
  });

  if (error) throw new Error(error.message);
  notifyBookingFinancialChange(data as string);
}

export async function updateBookingPayment(
  paymentId: string,
  updates: {
    amount: number;
    payment_method_id: number;
    paid_at: string;
    notes?: string | null;
  },
  reason: string,
): Promise<void> {
  const { data, error } = await supabase.rpc('update_booking_payment', {
    p_payment_id: paymentId,
    p_amount: updates.amount,
    p_payment_method_id: updates.payment_method_id,
    p_paid_at: updates.paid_at,
    p_notes: updates.notes ?? null,
    p_reason: reason,
  });

  if (error) throw new Error(error.message);
  notifyBookingFinancialChange(data as string);
}

export async function deleteBookingPayment(
  paymentId: string,
  reason: string,
): Promise<void> {
  const { data, error } = await supabase.rpc('delete_booking_payment', {
    p_payment_id: paymentId,
    p_reason: reason,
  });

  if (error) throw new Error(error.message);
  notifyBookingFinancialChange(data as string);
}

export async function sendBookingStatusEmail(
  bookingId: string,
  eventType: AccommodationBookingStatus,
): Promise<void> {
  const { error } = await supabase.functions.invoke('send-booking-email', {
    body: { booking_id: bookingId, event_type: eventType },
  });
  if (error) throw new Error(error.message ?? 'Failed to send email');
}

export async function performEarlyCheckout(
  params: EarlyCheckoutParams,
): Promise<AccommodationBooking> {
  const {
    bookingId,
    rooms,
    newCheckOut,
    newTotalAmount,
    allRoomsCheckedOut,
    notifyGuest,
  } = params;

  // 1. Update each room in accommodation_booking_rooms in parallel
  await Promise.all(
    rooms.map(async (r) => {
      const { error: roomError } = await supabase
        .from('accommodation_booking_rooms')
        .update({
          nights: r.nights,
          subtotal: r.subtotal,
        })
        .eq('id', r.bookingRoomId);

      if (roomError) {
        throw new Error(
          `Failed to update room ${r.roomId}: ${roomError.message}`,
        );
      }
    }),
  );

  const status: AccommodationBookingStatus = allRoomsCheckedOut
    ? 'checked_out'
    : 'checked_in';

  // 2. Update booking level details
  const { error: bookingError } = await supabase
    .from('accommodation_bookings')
    .update({
      check_out: newCheckOut,
      total_amount: newTotalAmount,
      status,
    })
    .eq('id', bookingId);

  if (bookingError) {
    throw new Error(`Failed to update booking: ${bookingError.message}`);
  }

  // 3. Log event manually (to capture early checkout metadata)
  const { data: sessionData } = await supabase.auth.getSession();
  const userId = sessionData?.session?.user?.id;

  const { error: eventError } = await supabase
    .from('accommodation_booking_events')
    .insert({
      booking_id: bookingId,
      from_status: 'checked_in' as AccommodationBookingStatus,
      to_status: status,
      notes: 'Early checkout — room dates updated',
      metadata: {
        early_checkout: true,
        rooms,
        original_check_out: params.originalCheckOut,
      },
      ...(userId ? { triggered_by: userId } : {}),
    });

  // We won't crash if event logging fails, but we will print a warning or handle it.
  if (eventError) {
    // eslint-disable-next-line no-console
    console.error('Failed to write early checkout audit event:', eventError);
  }

  // 4. Send email if requested
  if (notifyGuest) {
    try {
      await sendBookingStatusEmail(bookingId, 'checked_out');
    } catch (emailErr) {
      // eslint-disable-next-line no-console
      console.error(
        'Failed to send early checkout email notification:',
        emailErr,
      );
    }
  }

  return fetchBookingById(bookingId);
}

// ----------------------------------------------------------------
// Outstanding sales orders for a client (company / credit settlements)
// ----------------------------------------------------------------

export interface ClientOutstandingOrder {
  id: string;
  order_id: string;
  bill_number: number;
  sale_date: string;
  table_name: string | null;
  section_name: string | null;
  amount: number;
  payment_method_name: string;
  notes: string | null;
}

export async function fetchClientOutstandingOrders(
  clientId: string,
): Promise<ClientOutstandingOrder[]> {
  const { data, error } = await supabase
    .from('sales_creditor_settlements_view')
    .select(
      'id, order_id, bill_number, sale_date, table_name, section_name, amount, payment_method_name, notes',
    )
    .eq('client_id', clientId)
    .eq('payment_method_name', 'CREDIT')
    .order('sale_date', { ascending: false });

  if (error) throw new Error(error.message);
  return (data ?? []) as ClientOutstandingOrder[];
}

// ----------------------------------------------------------------
// Accommodation guests (accommodation_guests view)
// ----------------------------------------------------------------

export async function fetchAccommodationGuests(
  outletId?: string,
): Promise<AccommodationGuest[]> {
  let query = supabase
    .from('accommodation_guests')
    .select('*')
    .order('last_visit_date', { ascending: false, nullsFirst: false });

  if (outletId) {
    query = query.eq('outlet_id', outletId);
  }

  const { data, error } = await query;
  if (error) throw new Error(error.message);
  return (data ?? []) as AccommodationGuest[];
}

export async function fetchAccommodationGuestDetails(
  guestId: string,
  outletId?: string | null,
): Promise<AccommodationGuestDetails> {
  const { data, error } = await supabase.functions.invoke(
    'accommodation-guest-details',
    {
      body: {
        guest_id: guestId,
        outlet_id: outletId ?? null,
      },
    },
  );

  if (error) {
    throw new Error(error.message ?? 'Failed to load guest details');
  }

  if (!data?.guest) {
    throw new Error('Guest details response was empty');
  }

  return data.guest as AccommodationGuestDetails;
}

// ----------------------------------------------------------------
// Report views
// ----------------------------------------------------------------

export interface OccupancyReportRow {
  property_id: string;
  outlet_id: string;
  property_name: string;
  currency: string;
  stay_date: string;
  total_rooms: number | null;
  rooms_occupied: number;
  occupancy_pct: number | null;
}

export interface RevenueReportRow {
  booking_id: string;
  property_id: string;
  outlet_id: string;
  property_name: string;
  currency: string;
  check_in: string;
  check_out: string;
  nights: number;
  status: string;
  total_amount: number;
  amount_paid: number;
  balance_due: number;
  adults: number;
  children: number;
  source_name: string | null;
  client_first_name: string | null;
  client_last_name: string | null;
  client_email: string | null;
  client_nationality: string | null;
  created_at: string;
}

export interface BookingSourceReportRow {
  property_id: string;
  outlet_id: string;
  property_name: string;
  currency: string;
  source_name: string;
  total_bookings: number;
  confirmed_bookings: number;
  cancelled_bookings: number;
  total_revenue: number;
  total_collected: number;
}

export interface NightAuditReportRow {
  property_id: string;
  outlet_id: string;
  property_name: string;
  currency: string;
  audit_date: string;
  event_type: 'check_in' | 'check_out';
  booking_count: number;
  revenue: number | null;
  collected: number | null;
}

export interface RevParReportRow {
  property_id: string;
  outlet_id: string;
  property_name: string;
  currency: string;
  report_date: string;
  total_rooms_available: number;
  rooms_occupied: number;
  total_room_revenue: number;
  occupancy_pct: number;
  revpar: number;
}

export async function fetchOccupancyReport(
  outletId: string | null,
  dateFrom: string,
  dateTo: string,
): Promise<OccupancyReportRow[]> {
  let query = supabase
    .from('accommodation_occupancy_report')
    .select('*')
    .gte('stay_date', dateFrom)
    .lte('stay_date', dateTo)
    .order('stay_date');
  if (outletId) query = query.eq('outlet_id', outletId);
  const { data, error } = await query;
  if (error) throw new Error(error.message);
  return (data ?? []) as OccupancyReportRow[];
}

export async function fetchRevenueReport(
  outletId: string | null,
  dateFrom: string,
  dateTo: string,
): Promise<RevenueReportRow[]> {
  let query = supabase
    .from('accommodation_revenue_report')
    .select('*')
    .gte('check_in', dateFrom)
    .lte('check_in', dateTo)
    .order('check_in', { ascending: false });
  if (outletId) query = query.eq('outlet_id', outletId);
  const { data, error } = await query;
  if (error) throw new Error(error.message);
  return (data ?? []) as RevenueReportRow[];
}

export async function fetchBookingSourceReport(
  outletId: string | null,
): Promise<BookingSourceReportRow[]> {
  let query = supabase
    .from('accommodation_booking_source_report')
    .select('*')
    .order('total_revenue', { ascending: false });
  if (outletId) query = query.eq('outlet_id', outletId);
  const { data, error } = await query;
  if (error) throw new Error(error.message);
  return (data ?? []) as BookingSourceReportRow[];
}

export async function fetchNightAuditReport(
  outletId: string | null,
  dateFrom: string,
  dateTo: string,
): Promise<NightAuditReportRow[]> {
  let query = supabase
    .from('accommodation_night_audit_report')
    .select('*')
    .gte('audit_date', dateFrom)
    .lte('audit_date', dateTo)
    .order('audit_date')
    .order('event_type');
  if (outletId) query = query.eq('outlet_id', outletId);
  const { data, error } = await query;
  if (error) throw new Error(error.message);
  return (data ?? []) as NightAuditReportRow[];
}

// ─── OTA Payouts ──────────────────────────────────────────────────────────────

export interface OtaPayoutRecord {
  id: string;
  property_id: string;
  platform: 'booking_com' | 'airbnb';
  payout_reference: string;
  gross_amount: number;
  commission_amount: number;
  net_amount: number;
  currency: string;
  received_at: string;
  created_at: string;
}

export interface RecordOtaPayoutPayload {
  property_id: string;
  platform: 'booking_com' | 'airbnb';
  payout_reference: string;
  gross_amount: number;
  commission_amount: number;
  net_amount: number;
  currency: string;
  received_at: string;
}

export async function fetchOtaPayouts(
  propertyId: string,
): Promise<OtaPayoutRecord[]> {
  const { data, error } = await supabase
    .from('accommodation_ota_payouts' as never)
    .select('*')
    .eq('property_id', propertyId)
    .order('received_at', { ascending: false });
  if (error) throw new Error(error.message);
  return (data ?? []) as OtaPayoutRecord[];
}

export async function recordOtaPayout(
  payload: RecordOtaPayoutPayload,
): Promise<OtaPayoutRecord> {
  const { data, error } = await supabase
    .from('accommodation_ota_payouts' as never)
    .insert(payload as never)
    .select()
    .single();
  if (error) throw new Error(error.message);
  return data as OtaPayoutRecord;
}

export async function runNightAudit(
  propertyId: string,
  stayDate: string,
): Promise<{ processed: number }> {
  const { data, error } = await supabase.rpc(
    'run_accommodation_night_audit_revenue_recognition' as never,
    { p_property_id: propertyId, p_stay_date: stayDate } as never,
  );
  if (error) throw new Error(error.message);
  return { processed: typeof data === 'number' ? data : 0 };
}

export async function fetchRevParReport(
  propertyId: string,
  dateFrom: string,
  dateTo: string,
): Promise<RevParReportRow[]> {
  const { data, error } = await supabase
    .from('accommodation_revpar_report')
    .select('*')
    .eq('property_id', propertyId)
    .gte('report_date', dateFrom)
    .lte('report_date', dateTo)
    .order('report_date');

  if (error) throw new Error(error.message);
  return (data ?? []) as RevParReportRow[];
}

// ----------------------------------------------------------------
// Room Revenue Matrix (client-side pivoted report)
// ----------------------------------------------------------------

export interface RoomRevenueMatrixRoom {
  room_id: string;
  room_number: string;
  room_type_id: string;
  room_type_name: string;
  room_type_sort: number;
}

export interface RoomRevenueMatrixBooking {
  booking_id: string;
  room_id: string;
  check_in: string;
  check_out: string;
  /** daily_rate = total_amount / nights, spread across all booked rooms */
  daily_rate: number;
}

export interface RoomRevenueMatrixData {
  rooms: RoomRevenueMatrixRoom[];
  bookings: RoomRevenueMatrixBooking[];
}

export async function fetchRoomRevenueMatrix(
  propertyId: string,
  dateFrom: string,
  dateTo: string,
): Promise<RoomRevenueMatrixData> {
  const [roomsResult, bookingsResult] = await Promise.all([
    supabase
      .from('accommodation_rooms')
      .select(
        'id, room_number, room_type_id, room_type:accommodation_room_types(id, name)',
      )
      .eq('property_id', propertyId)
      .eq('is_active', true)
      .order('room_number'),
    supabase
      .from('accommodation_bookings')
      .select(
        'id, check_in, check_out, total_amount, status, booking_rooms:accommodation_booking_rooms(room_id)',
      )
      .eq('property_id', propertyId)
      .not('status', 'in', '("cancelled","no_show")')
      .lte('check_in', dateTo)
      .gt('check_out', dateFrom),
  ]);

  if (roomsResult.error) throw new Error(roomsResult.error.message);
  if (bookingsResult.error) throw new Error(bookingsResult.error.message);

  const rooms: RoomRevenueMatrixRoom[] = (roomsResult.data ?? []).map(
    (r: any) => ({
      room_id: r.id,
      room_number: r.room_number,
      room_type_id: r.room_type_id,
      room_type_name: (r.room_type as any)?.name ?? 'Unknown',
      room_type_sort: 0, // accommodation_room_types has no sort_order; groups are sorted by name
    }),
  );

  const bookings: RoomRevenueMatrixBooking[] = (
    (bookingsResult.data ?? []) as any[]
  )
    .filter((b: any) => {
      const roomIds: string[] = (b.booking_rooms ?? []).map(
        (br: any) => br.room_id,
      );
      return roomIds.length > 0;
    })
    .flatMap((b: any) => {
      const roomIds: string[] = (b.booking_rooms ?? []).map(
        (br: any) => br.room_id,
      );
      const nights = Math.max(
        1,
        (new Date(b.check_out).getTime() - new Date(b.check_in).getTime()) /
          86_400_000,
      );
      // Split cost evenly across booked rooms then by nights
      const dailyRatePerRoom = b.total_amount / roomIds.length / nights;
      return roomIds.map((roomId: string) => ({
        booking_id: b.id,
        room_id: roomId,
        check_in: b.check_in,
        check_out: b.check_out,
        daily_rate: dailyRatePerRoom,
      }));
    });

  return { rooms, bookings };
}

// ----------------------------------------------------------------
// Dashboard stats (calls the get_accommodation_dashboard_stats RPC)
// ----------------------------------------------------------------
export async function fetchDashboardStats(
  propertyId: string,
  dateFrom: string,
  dateTo: string,
): Promise<AccommodationDashboardStats> {
  const { data, error } = await supabase.rpc(
    'get_accommodation_dashboard_stats',
    {
      p_property_id: propertyId,
      p_date_from: dateFrom,
      p_date_to: dateTo,
    },
  );

  if (error) throw new Error(error.message);
  return data as AccommodationDashboardStats;
}

// ----------------------------------------------------------------
// Guest reviews
// ----------------------------------------------------------------

export async function fetchReviews(
  propertyId: string,
  page = 1,
  pageSize = 20,
): Promise<{ data: GuestReviewAnalytics[]; total: number }> {
  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;

  const { data, error, count } = await supabase
    .from('accommodation_reviews_analytics')
    .select('*', { count: 'exact' })
    .eq('property_id', propertyId)
    .order('submitted_at', { ascending: false })
    .range(from, to);

  if (error) throw new Error(error.message);
  return { data: (data ?? []) as GuestReviewAnalytics[], total: count ?? 0 };
}

export async function fetchReviewStats(
  propertyId: string,
  dateFrom?: string,
  dateTo?: string,
): Promise<ReviewStats> {
  const { data, error } = await supabase.rpc(
    'get_accommodation_reviews_stats',
    {
      p_property_id: propertyId,
      p_date_from: dateFrom ?? null,
      p_date_to: dateTo ?? null,
    },
  );

  if (error) throw new Error(error.message);
  return (data ?? {}) as ReviewStats;
}

export async function fetchReviewMonthlyTrend(
  propertyId: string,
  months = 12,
): Promise<ReviewMonthlyTrend[]> {
  const { data, error } = await supabase.rpc(
    'get_accommodation_reviews_monthly_trend',
    { p_property_id: propertyId, p_months: months },
  );

  if (error) throw new Error(error.message);
  return (data ?? []) as ReviewMonthlyTrend[];
}
